import React, { useState, useEffect } from 'react';
import { Modal, ConfirmDialog, Pagination, AccessDeniedMessage, ContentSearchBar, ContentsPageHeader, ReportsModalContent, AccessDeniedModalContent, SuccessMessage } from '../../molecules';
import { ContentsList } from '../../organisms';
import { contentsService, type Content } from '../../../services/contentsService';

export const ContentsPage = () => {
  const [contents, setContents] = useState<Content[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const [requiredRoles, setRequiredRoles] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedContent, setSelectedContent] = useState<Content | null>(null);
  const [reports, setReports] = useState<any[]>([]);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contentToDelete, setContentToDelete] = useState<Content | null>(null);
  const [filterHasReports, setFilterHasReports] = useState(false);
  const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState<string>('');
  const [accessDeniedRoles, setAccessDeniedRoles] = useState<string[]>([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState(true);
  const [triggeringConversionsId, setTriggeringConversionsId] = useState<number | null>(null);

  const loadContents = async (
    page: number = 1,
    search?: string,
    hasReports?: boolean,
  ) => {
    try {
      setLoading(true);
      setError(null);
      const response = await contentsService.searchContents(
        search,
        page,
        20,
        hasReports,
      );
      setContents(response.data);
      setTotalPages(response.totalPages);
      setCurrentPage(response.page);
    } catch (err: any) {
      // Check if it's a 403 Forbidden error
      if (err?.response?.status === 403 || err?.message === 'Erişim Reddedildi') {
        setIsAccessDenied(true);
        setError(null);
        // Extract required roles from error response if available
        const roles = err?.response?.data?.requiredRoles ||
          err?.response?.data?.roles ||
          ['superuser', 'moderator'];
        setRequiredRoles(Array.isArray(roles) ? roles : []);
      } else {
        let errorMessage = 'İçerikler yüklenirken bir hata oluştu';
        if (err?.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err?.message) {
          errorMessage = err.message;
        } else if (typeof err === 'string') {
          errorMessage = err;
        }
        setError(errorMessage);
        setIsAccessDenied(false);
      }
      console.error('Failed to load contents:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContents(1, searchQuery, filterHasReports);
  }, [searchQuery, filterHasReports]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadContents(1, searchQuery, filterHasReports);
  };

  const handleFilterToggle = () => {
    setFilterHasReports(!filterHasReports);
    setCurrentPage(1);
  };

  const handleDeleteClick = (content: Content) => {
    setContentToDelete(content);
    setShowDeleteConfirm(true);
  };

  const handleTriggerConversions = async (content: Content) => {
    try {
      setTriggeringConversionsId(content.id);
      await contentsService.triggerConversions(content.id);
      setIsSuccess(true);
      setSuccessMessage('Medya dönüşüm işlemleri başarıyla tetiklendi.');
      setShowSuccessModal(true);
    } catch (err: any) {
      setIsSuccess(false);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Medya dönüşüm işlemleri tetiklenirken bir hata oluştu';
      setSuccessMessage(errorMessage);
      setShowSuccessModal(true);
    } finally {
      setTriggeringConversionsId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!contentToDelete) return;

    try {
      setDeletingId(contentToDelete.id);
      setShowDeleteConfirm(false);
      await contentsService.deleteContent(contentToDelete.id);

      const shouldGoToPreviousPage = contents.length === 1 && currentPage > 1;
      const pageToLoad = shouldGoToPreviousPage ? currentPage - 1 : currentPage;

      await loadContents(pageToLoad, searchQuery, filterHasReports);
    } catch (err: any) {
      console.error('Failed to delete content:', err);
      // Check if it's a 403 Forbidden error
      if (err?.response?.status === 403 || err?.message === 'Erişim Reddedildi') {
        const roles = err?.response?.data?.requiredRoles ||
          err?.response?.data?.roles ||
          ['superuser'];
        setAccessDeniedMessage('Bu içeriği silmek için gerekli yetkilere sahip değilsiniz.');
        setAccessDeniedRoles(Array.isArray(roles) ? roles : []);
        setShowAccessDeniedModal(true);
      } else {
        alert(err instanceof Error ? err.message : 'İçerik silinirken bir hata oluştu');
      }
    } finally {
      setDeletingId(null);
      setContentToDelete(null);
    }
  };

  const handleViewReports = async (content: Content) => {
    console.log('[handleViewReports] Opening reports for content:', content.id);
    setSelectedContent(content);
    setShowReportsModal(true);
    setReportsLoading(true);
    setReports([]);

    try {
      console.log('[handleViewReports] Fetching reports for content:', content.id);
      const contentReports = await contentsService.getContentReports(content.id);
      console.log('[handleViewReports] Received reports:', contentReports);
      console.log('[handleViewReports] Reports count:', contentReports.length);
      setReports(contentReports);
    } catch (err) {
      console.error('[handleViewReports] Failed to load reports:', err);
      setReports([]);
    } finally {
      setReportsLoading(false);
    }
  };

  // Show AccessDenied in full screen like dashboard
  if (isAccessDenied) {
    return (
      <AccessDeniedMessage
        message="Bu içeriklere erişmek için gerekli yetkilere sahip değilsiniz."
        requiredRoles={requiredRoles}
        onBack={() => window.history.back()}
      />
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-gray-50 to-white min-h-screen">
      <ContentsPageHeader />

      <ContentSearchBar
        searchQuery={searchQuery}
        filterHasReports={filterHasReports}
        onSearchQueryChange={setSearchQuery}
        onFilterToggle={handleFilterToggle}
        onSearch={handleSearch}
      />

      {/* Content List */}
      {loading ? (
        <div className="text-center text-gray-500 py-8">İçerikler yükleniyor...</div>
      ) : error ? (
        <div className="text-center text-red-500 py-8">{error}</div>
      ) : contents.length === 0 ? (
        <div className="text-center text-gray-500 py-8">İçerik bulunamadı</div>
      ) : (
        <>
          <ContentsList
            contents={contents}
            deletingId={deletingId}
            onViewReports={handleViewReports}
            onDeleteClick={handleDeleteClick}
            onTriggerConversions={handleTriggerConversions}
            triggeringConversionsId={triggeringConversionsId}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => loadContents(page, searchQuery, filterHasReports)}
          />
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setContentToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="İçeriği Sil"
        message={
          <div>
            {contentToDelete?.title ?
              <p className="mb-3 font-semibold text-gray-800">
                {contentToDelete?.title}
              </p>
              : null}
            {contentToDelete?.content && (
              <p className="mb-3 text-sm text-gray-600 line-clamp-3">
                {contentToDelete.content}
              </p>
            )}
            <p className="text-gray-700">
              Bu içeriği silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
          </div>
        }
        confirmText="Sil"
        cancelText="İptal"
        variant="danger"
        isLoading={deletingId !== null}
      />

      {/* Access Denied Modal */}
      <Modal
        isOpen={showAccessDeniedModal}
        onClose={() => setShowAccessDeniedModal(false)}
        title="Erişim Reddedildi"
        size="medium"
      >
        <AccessDeniedModalContent
          message={accessDeniedMessage}
          requiredRoles={accessDeniedRoles}
          onClose={() => setShowAccessDeniedModal(false)}
        />
      </Modal>

      {/* Reports Modal */}
      <Modal
        isOpen={showReportsModal}
        onClose={() => setShowReportsModal(false)}
        title={`Raporlar - ${selectedContent?.title || ''}`}
        size="large"
      >
        <ReportsModalContent
          reports={reports}
          isLoading={reportsLoading}
          contentTitle={selectedContent?.title}
          onClose={() => setShowReportsModal(false)}
        />
      </Modal>

      {/* Success/Error Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={isSuccess ? 'Bilgi' : 'Hata'}
        size="small"
      >
        <SuccessMessage
          message={successMessage}
          onClose={() => setShowSuccessModal(false)}
          isSuccess={isSuccess}
        />
      </Modal>
    </div>
  );
};
